import { Badge, Card } from '@salesforce/design-system-react';

import type { QualityFormulaPreviewResult } from '../qualityFormulaTypes';

import { ResultField } from './ResultField';
import { formatCurrency, formatNumber, getDirectionColor, getTotalImpactColor } from './utils/resultFormatting';

interface ImpactCardProps {
  inline?: boolean;
  result: QualityFormulaPreviewResult;
}

const formatOptionalNumber = (value: null | number | undefined): string =>
  value === null || value === undefined ? '—' : formatNumber(value);

const PriceImpactFields: React.FC<{ directionColor: string; result: QualityFormulaPreviewResult }> = ({
  directionColor,
  result,
}) => (
  <>
    <div className="slds-col slds-size_1-of-2">
      <ResultField label="Matched Tier" value={result.matchedTier === null ? '—' : `#${result.matchedTier}`} />
    </div>
    <div className="slds-col slds-size_1-of-2">
      <ResultField
        color={directionColor}
        label="Price Change / Unit"
        value={result.unitImpact === null ? '—' : formatCurrency(result.unitImpact)}
      />
    </div>
  </>
);

const QuantityImpactFields: React.FC<{ result: QualityFormulaPreviewResult }> = ({ result }) => (
  <>
    <div className="slds-col slds-size_1-of-2">
      <ResultField label="Delivered Qty" value={formatOptionalNumber(result.shippedQuantity)} />
    </div>
    <div className="slds-col slds-size_1-of-2">
      <ResultField label="New Qty" value={formatOptionalNumber(result.adjustedInvoiceQuantity)} />
    </div>
  </>
);

// Renders a single price- or quantity-impact result. Kind-specific fields are the only branch —
// everything else (badge, title, description, total) is shared markup for both kinds.
export const ImpactCard: React.FC<ImpactCardProps> = ({ inline = false, result }) => {
  const isPrice = result.kind === 'price';
  const directionColor = getDirectionColor(result.direction);
  const totalLabel = isPrice ? 'Total Price Impact' : 'Quantity Reduced';
  const totalValue =
    result.totalImpact === null
      ? null
      : `${isPrice ? formatCurrency(result.totalImpact) : formatNumber(result.totalImpact)}${isPrice ? '' : ' units'}`;

  const fields = (
    <div className="slds-grid slds-wrap slds-gutters_small">
      <div className="slds-col slds-size_1-of-2">
        <ResultField label="Formula Applied" value={result.formulaLabel} />
      </div>
      {result.incrementLabel ? (
        <div className="slds-col slds-size_1-of-2">
          <ResultField label="Increment" value={result.incrementLabel} />
        </div>
      ) : null}
      <div className="slds-col slds-size_1-of-2">
        <ResultField label="Test Value" value={formatNumber(result.testValue)} />
      </div>
      <div className="slds-col slds-size_1-of-2">
        <ResultField label="Contracted Value" value={formatNumber(result.contractedValue)} />
      </div>
      {isPrice ? (
        <PriceImpactFields directionColor={directionColor} result={result} />
      ) : (
        <QuantityImpactFields result={result} />
      )}
    </div>
  );

  if (inline) {
    return (
      <div className="qfw-impact-row">
        <div className="qfw-impact-row-heading">
          <strong>{result.specLabel}</strong>
          <Badge color={isPrice ? 'default' : 'light'} content={isPrice ? 'Price Impact' : 'Quantity Impact'} />
        </div>
        <div className="qfw-impact-outcome">
          <span className="slds-text-color_weak">{totalLabel}</span>
          <strong style={{ color: getTotalImpactColor(result) }}>{totalValue ?? '—'}</strong>
        </div>
        <details className="qfw-impact-details">
          <summary>Calculation details</summary>
          <p className="qfw-impact-detail-description slds-text-body_small slds-text-color_weak">
            {result.description}
          </p>
          {fields}
        </details>
      </div>
    );
  }

  return (
    <Card
      className="qfw-result-card"
      headerActions={
        <Badge color={isPrice ? 'default' : 'light'} content={isPrice ? 'Price Impact' : 'Quantity Impact'} />
      }
      heading={result.specLabel}
    >
      <p className="slds-text-body_small slds-text-color_weak slds-p-bottom_small">{result.description}</p>
      {fields}
      {totalValue !== null ? (
        <>
          <hr className="slds-m-vertical_small" />
          <div className="slds-grid slds-grid_align-spread">
            <span className="slds-text-title_caps slds-text-color_weak">{totalLabel}</span>
            <span className="slds-text-heading_small" style={{ color: getTotalImpactColor(result) }}>
              {totalValue}
            </span>
          </div>
        </>
      ) : null}
    </Card>
  );
};
